import { useContext, useMemo } from 'react';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import PowerSettingsNewOutlinedIcon from '@mui/icons-material/PowerSettingsNewOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import { TitleMediumEmphasized, BodySmall } from '@/design-system';
import { AccessibleList, AccessibleListItem, Divider, PageTitleHelmet } from '@/components';
import { APP_ROUTES } from '@/config/constants';
import { AccountCard } from './components/AccountCard/AccountCard';
import styles from './Account.styles';
import UserPreferencesContext from '@/context/UserPreferencesContext';
import { AccountSwitcher } from './components/AccountSwitcher/AccountSwitcher';
import { userInformationVar } from '@/graphql/state';
import { useReactiveVar } from '@apollo/client';
import { useLinkedAccounts } from '@/hooks';

export const Account: React.FC = () => {
  const { language } = useContext(UserPreferencesContext);
  const user = useReactiveVar(userInformationVar);
  const userName = useMemo(
    () => (user?.firstName && user?.lastName ? `${user?.firstName} ${user?.lastName}` : 'Parent'),
    [user]
  );

  const { linkedAccounts } = useLinkedAccounts();
  const filteredAccounts = linkedAccounts.filter(account => account.userId !== user?.id);
  const list = useMemo(
    () => [
      {
        icon: <AccountCircleOutlinedIcon />,
        label: 'Account Information',
        route: APP_ROUTES.accountInformation,
      },
      {
        icon: <TuneOutlinedIcon />,
        label: 'Preference',
        route: APP_ROUTES.preference,
      },
      {
        icon: <HelpOutlineOutlinedIcon />,
        label: 'Help',
        route: `https://info.peachjar.com/${language.code === 'es' ? 'es' : 'en'}/help-center`,
        isExternal: true,
      },
      {
        icon: <AssignmentOutlinedIcon />,
        label: 'Terms & Policies',
        route: 'https://peachjar.com/privacy-policy-terms.html#terms',
        isExternal: true,
      },
      {
        icon: <PowerSettingsNewOutlinedIcon />,
        label: 'Logout',
        route: APP_ROUTES.login,
      },
    ],
    [language]
  );

  return (
    <>
      <PageTitleHelmet>Account</PageTitleHelmet>
      <header className={styles.header} data-testid="accountInformationHeader">
        {filteredAccounts.length > 0 ? (
          <AccountSwitcher
            linkedAccounts={filteredAccounts}
            userName={userName}
            userEmail={user?.email}
          />
        ) : (
          <>
            <TitleMediumEmphasized data-testid="userName" as="h1">
              {userName}
            </TitleMediumEmphasized>
            <BodySmall data-testid="userEmail">{user?.email}</BodySmall>
          </>
        )}
      </header>
      <Divider />
      <main className={styles.container} data-testid="accountList">
        <AccessibleList>
          {list.map(({ label, ...listProps }) => (
            <AccessibleListItem key={label} block>
              <AccountCard label={label} {...listProps} showArrow={label !== 'Logout'} />
            </AccessibleListItem>
          ))}
        </AccessibleList>
      </main>
    </>
  );
};
